// 📄 data/commerceChecklistCatalog.ts
// Fuente única de "qué checklists/pasos existen en Commerce Automation",
// usada para vincular un Incidente a un paso concreto (IncidenteFormModal,
// IncidenteDetailModal) y para la alerta de incidentes sin resolver en PRD
// (useChecklistIncidentAlerts). Los ids replican exactamente los que ya usan
// como clave de Firestore useGeneralConfig.ts, configuracionGeneral.tsx,
// ordenCompra.tsx, avisoExpedicion.tsx y cutover.tsx — si se agrega un paso
// nuevo en esos archivos, agrégalo también acá.

export interface ChecklistStepDef {
  id: string;
  title: string;
}

export interface ChecklistCanalDef {
  id: string;
  label: string;
  steps: ChecklistStepDef[];
}

export interface ChecklistDef {
  id: string;
  label: string;
  canales?: ChecklistCanalDef[];
  steps?: ChecklistStepDef[];
}

export const COMMERCE_CHECKLISTS: ChecklistDef[] = [
  {
    id: 'generals',
    label: 'Configuraciones Generales',
    canales: [
      {
        id: 'erp',
        label: 'ERP (Add-on)',
        // Incluye los pasos de ambas variantes (on-premise y S/4HANA
        // Cloud, ver esErpCloud en useGeneralConfig.ts) — no se filtra por
        // tipo de ERP acá, así un incidente siempre puede vincularse al
        // paso real que se estaba ejecutando.
        steps: [
          { id: 'certificates', title: '1. Certificados SSL' },
          { id: 'rfc', title: '2. Conexión RFC / HTTP' },
          { id: 'port', title: '3. Port Definition' },
          { id: 'logicalsystem', title: '4. Logical System' },
          { id: 'activation', title: '5. Activación de Canales' },
          { id: 'cloud_comm_user', title: 'Cloud · 1. Usuario de Comunicación' },
          { id: 'cloud_system_id', title: 'Cloud · 2. System ID' },
          { id: 'cloud_comm_arrangement', title: 'Cloud · 3. Acuerdo de Comunicación' },
          { id: 'cloud_cig_url', title: 'Cloud · 4. URL de CIG' },
          { id: 'cloud_comm_scenario', title: 'Cloud · 5. Communication Scenario / APIs' },
          { id: 'cloud_test_connection', title: 'Cloud · 6. Prueba de Conexión' }
        ]
      },
      {
        id: 'cloudconnector',
        label: 'Cloud Connector',
        steps: [
          { id: 'cc_subaccount', title: '1. Add Subaccount (BTP Setup)' },
          { id: 'cc_virtual_host', title: '2. Cloud to On-Premise Mapping' },
          { id: 'cc_resources', title: '3. Access Control (Resources)' },
          { id: 'cc_tunnel', title: '4. Estado del Túnel (Handshake)' }
        ]
      },
      {
        id: 'ariba',
        label: 'Ariba Portal',
        steps: [
          { id: 'ap_site_config', title: '1. Parámetros del Sitio' },
          { id: 'ap_channels', title: '2. Switches de Canales' },
          { id: 'ap_testing', title: '3. Test de Conectividad' }
        ]
      },
      {
        id: 'gateway',
        label: 'Managed Gateway (CIG)',
        steps: [
          { id: 'mg_project', title: '1. Proyecto CIG' },
          { id: 'mg_connections', title: '2. Conexiones SOAP/REST' },
          { id: 'mg_global_mapping', title: '3. Mapeos Globales' }
        ]
      }
    ]
  },
  {
    id: 'purchaseOrder',
    label: 'Orden de Compra',
    steps: [
      { id: 'po_output', title: '1. Output Determination' },
      { id: 'po_partner', title: '2. Partner Profile & Port' },
      { id: 'po_distmodel', title: '3. Distribution Model' },
      { id: 'po_mapping', title: '4. CIG Custom Mapping' },
      { id: 'po_test', title: '5. Pruebas End-to-End' }
    ]
  },
  {
    id: 'asn',
    label: 'Aviso de Expedición',
    steps: [
      { id: 'asn_idoc', title: '1. Inbound IDoc Setup' },
      { id: 'asn_process', title: '2. Process Code & Function Module' },
      { id: 'asn_mapping', title: '3. CIG Gateway Mapping' },
      { id: 'asn_delivery', title: '4. Monitoreo de Entregas' },
      { id: 'asn_test', title: '5. Pruebas End-to-End' }
    ]
  },
  {
    id: 'orderConfirmation',
    label: 'Confirmación de Pedido',
    steps: [
      { id: 'confirm_params', title: '1. Maintain Parameters' },
      { id: 'confirm_exit', title: '2. Implement Custom Exit' },
      { id: 'confirm_control', title: '3. Set Up Confirmation Control' },
      { id: 'confirm_job', title: '4. Schedule Background Job' }
    ]
  },
  {
    id: 'goodsReceipt',
    label: 'Recepción de Mercancía',
    steps: [
      { id: 'receipt_output', title: '1. Define Message Output Control' },
      { id: 'receipt_params', title: '2. Maintain Parameters' }
    ]
  },
  {
    id: 'serviceEntrySheet',
    label: 'HES (SAP Ariba)',
    steps: [
      { id: 'hes_params', title: '1. Maintain Parameters' },
      { id: 'hes_badis', title: '2. Implement BAdIs / Exits' }
    ]
  },
  {
    id: 'serviceEntrySheetResponse',
    label: 'HES - ERP',
    steps: [
      { id: 'hes_erp_output', title: '1. Message Output Control' },
      { id: 'hes_erp_params', title: '2. Maintain Parameters' }
    ]
  },
  {
    id: 'invoice',
    label: 'Facturas',
    steps: [
      { id: 'invoice_params', title: '1. Maintain Parameters' },
      { id: 'invoice_tax', title: '2. Company Code & Tax Codes' },
      { id: 'invoice_posting', title: '3. Program Parameters & Posting Keys' },
      { id: 'invoice_exits', title: '4. Customer Exits & BTE Linkages' }
    ]
  },
  {
    id: 'invoiceStatus',
    label: 'Estatus de Facturas',
    steps: [
      { id: 'isu_params', title: '1. Maintain Parameters' },
      { id: 'isu_bte', title: '2. Configure BTE' },
      { id: 'isu_job', title: '3. Schedule Background Job' }
    ]
  },
  {
    id: 'cutover',
    label: 'Plan de Cutover',
    steps: [
      { id: 't1', title: 'Credenciales de Producción' },
      { id: 't2', title: 'Certificados SSL (STRUST)' },
      { id: 't3', title: 'Destinos RFC / HTTP' },
      { id: 't4', title: 'Code Freeze' },
      { id: 't5', title: 'Transporte de OTs' },
      { id: 't6', title: 'Registro de CIG' },
      { id: 't7', title: 'Modelo de Distribución & Partner Profile' },
      { id: 't8', title: 'Prueba de Conexión de Salida' },
      { id: 't9', title: 'Monitoreo de IDocs' },
      { id: 't10', title: 'Procesamiento cXML' },
      { id: 't11', title: 'Alertas y Logs de Excepciones' },
      { id: 't12', title: 'Handover a Soporte (Hypercare)' }
    ]
  }
];

export const buildChecklistStepKey = (checklistId: string, stepId: string, canalId?: string): string =>
  canalId ? `${checklistId}:${canalId}:${stepId}` : `${checklistId}:${stepId}`;

interface ParsedChecklistStepKey {
  checklist: ChecklistDef;
  canal?: ChecklistCanalDef;
  step: ChecklistStepDef;
}

export const parseChecklistStepKey = (key: string): ParsedChecklistStepKey | null => {
  const parts = key.split(':');
  const checklist = COMMERCE_CHECKLISTS.find(c => c.id === parts[0]);
  if (!checklist) return null;

  if (parts.length === 3) {
    const canal = checklist.canales?.find(c => c.id === parts[1]);
    const step = canal?.steps.find(s => s.id === parts[2]);
    if (!canal || !step) return null;
    return { checklist, canal, step };
  }

  if (parts.length === 2) {
    const step = checklist.steps?.find(s => s.id === parts[1]);
    if (!step) return null;
    return { checklist, step };
  }

  return null;
};

export const getChecklistStepLabel = (key: string): string => {
  const parsed = parseChecklistStepKey(key);
  if (!parsed) return key;
  const parts = [parsed.checklist.label, parsed.canal?.label, parsed.step.title].filter(Boolean);
  return parts.join(' › ');
};
